'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  type ComponentProps,
  type MouseEvent,
  type ReactNode,
  forwardRef,
} from 'react';
import { markPendingNavigation } from '~/components/Navigation/routeNavigationStore';

type PendingLinkProps = ComponentProps<typeof Link> & {
  children?: ReactNode;
  /** Skip pending skeleton (e.g. new-tab or in-place toggles). */
  skipPending?: boolean;
};

function hrefToString(href: ComponentProps<typeof Link>['href']): string {
  if (typeof href === 'string') return href;
  if (href && typeof href === 'object' && 'pathname' in href && typeof href.pathname === 'string') {
    return href.pathname;
  }
  return '';
}

/**
 * next/link that marks soft navigation pending so WorkspaceContentPane can
 * swap to a skeleton on the same frame as the click.
 */
const PendingLink = forwardRef<HTMLAnchorElement, PendingLinkProps>(
  function PendingLink({ onClick, skipPending, target, href, ...props }, ref) {
    const pathname = usePathname();

    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(e);
      // Modified / non-primary clicks don't navigate this tab — don't strand pending.
      if (
        e.defaultPrevented ||
        skipPending ||
        target === '_blank' ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      markPendingNavigation(hrefToString(href), pathname);
    };

    return <Link ref={ref} href={href} target={target} onClick={handleClick} {...props} />;
  },
);

export default PendingLink;
